import React, { memo } from 'react';

import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { Text, TextType } from 'ui-components/components/text';

import { SmartFillBanner } from '../smartfill-banner';
import { RestrictedShipmentSection } from '../../../../../../fulfillment-planner/restricted-shipment-config';

import {
  Confirmation,
  DutiesAndTaxesField,
  Insurance,
  LabeledField,
  Package,
  PackageContentDescription,
  Pickup,
  Recommendations,
  RequestedService,
  RestrictedShipmentBanner,
  ShipFrom,
  ShippingService,
  Size,
  Weight,
} from '../../../../configure-shipment/fields';
import { PackingRecommend } from '../../../../configure-shipment/fields/packing-recommend/packing-recommend';
import { useFulfillmentPlannerContext } from '../../../../../../fulfillment-planner/context';
import { useFulfillmentPlanRestrictionsContext } from '../../../restrictions-context/context';
import { FulfillmentPlanService } from '../../../../../../services/fulfillment-plan-service';
import { useShouldDisplaySmartFillBanner } from '../../../../../../hooks/smartfill/use-smartfill-data';
import { useEnableInsurancePromoExperiment } from '../../../../../../feature-flags/hooks/use-enable-insurance-promo-experiment';
import { InsurancePromo } from '../../../../../../fulfillment-planner/insurance-promo/insurance-promo';
import { ShipFromLabel } from '../../../../shared-field-labels/shared-field-labels';
import fulfillmentPlannerMessages from '../../../../../../fulfillment-planner/messages';

import style from '../../../../configure-shipment/fields/fields.module.less';
import sharedFieldStyle from '../../../../shared-field-labels/form-fields.module.less';
import MarketplaceServiceRecommendation from '../../../../../../fulfillment-planner/marketplace-service-recommendation';

const TikTokShipFrom = ({ containerClassName = '' }) => (
  <LabeledField label={ShipFromLabel}>
    <div className={containerClassName}>
      <Text
        className={sharedFieldStyle['readonly-value']}
        textType={TextType.Paragraph}
        value={<FormattedMessage {...fulfillmentPlannerMessages.tikTokShipFromText} />}
      />
    </div>
  </LabeledField>
);

/**
 * @param {object} props
 * @param {string} props.fulfillmentPlanId
 * @param {JSX.Element?} props.AdditionalContent
 * @param {boolean?} props.isTikTokShipping
 * @param {JSX.Element?} props.RestrictedBanner
 * @param {boolean?} props.showEnhancedConfig
 * @returns {JSX.Element}
 */
const RestrictedConfiguration = ({
  AdditionalContent = null,
  fulfillmentPlanId,
  isTikTokShipping = false,
  RestrictedBanner,
  showEnhancedConfig = false,
}) => {
  if (!showEnhancedConfig) {
    return (
      <>
        <ShipFrom fulfillmentPlanId={fulfillmentPlanId} shouldHideRateBrowser />
        <RestrictedShipmentBanner />
      </>
    );
  }
  return (
    <>
      {RestrictedBanner && <RestrictedBanner />}
      {isTikTokShipping ? (
        <TikTokShipFrom containerClassName={style['full-width-content']} />
      ) : (
        <ShipFrom containerClassName={style['full-width-content']} fulfillmentPlanId={fulfillmentPlanId} shouldHideRateBrowser />
      )}
      <Weight containerClassName={style['full-width-content']} isRequired={isTikTokShipping} />
      <Package
        containerClassName={style['full-width-content']}
        fulfillmentPlanId={fulfillmentPlanId}
        hideMultiPackage
        isDisabled={isTikTokShipping}
      />
      <Size
        AdditionalContent={AdditionalContent}
        containerClassName={style['full-width-content']}
        fulfillmentPlanId={fulfillmentPlanId}
        isRequired={isTikTokShipping}
      />
      <div />
    </>
  );
};

const ShipmentConfiguration = memo(({ fulfillmentPlanId }) => {
  const restrictions = useFulfillmentPlanRestrictionsContext();
  const isRestricted = FulfillmentPlanService.hasLabelConfigurationRestriction(restrictions);
  const { hasSmartFillDataBeenChanged, isSmartFillEnabled } = useFulfillmentPlannerContext();
  const isInsurancePromoExperimentEnabled = useEnableInsurancePromoExperiment();

  const hasAllSmartFillDataChanged = Object.values(hasSmartFillDataBeenChanged).every((val) => val);
  const shouldDisplaySmartFillBanner = useShouldDisplaySmartFillBanner(fulfillmentPlanId);
  const showSmartFillBanner = isSmartFillEnabled && shouldDisplaySmartFillBanner && !hasAllSmartFillDataChanged;

  if (isRestricted) {
    return <RestrictedShipmentSection Config={RestrictedConfiguration} fulfillmentPlanId={fulfillmentPlanId} />;
  }

  return (
    <>
      {showSmartFillBanner && <SmartFillBanner />}
      <PackingRecommend fulfillmentPlanId={fulfillmentPlanId} hideButton />
      <RequestedService fulfillmentPlanId={fulfillmentPlanId} />
      <ShipFrom fulfillmentPlanId={fulfillmentPlanId} />
      <Weight fulfillmentPlanId={fulfillmentPlanId} />
      <ShippingService fulfillmentPlanId={fulfillmentPlanId} />
      <MarketplaceServiceRecommendation />
      <Pickup fulfillmentPlanId={fulfillmentPlanId} />
      <Package fulfillmentPlanId={fulfillmentPlanId} />
      <PackageContentDescription />
      <Size fulfillmentPlanId={fulfillmentPlanId} />
      <Confirmation />
      {isInsurancePromoExperimentEnabled ? (
        <InsurancePromo headerClassName={style['insurance-promo-header']}>
          <Insurance fulfillmentPlanId={fulfillmentPlanId} isPartOfInsurancePromoExperiment rowClassName={style['full-width-content']} />
        </InsurancePromo>
      ) : (
        <Insurance fulfillmentPlanId={fulfillmentPlanId} />
      )}
      <DutiesAndTaxesField />
      <Recommendations />
    </>
  );
});

ShipmentConfiguration.propTypes = {
  fulfillmentPlanId: PropTypes.string.isRequired,
};

export { ShipmentConfiguration };
